// PII scrubbing for Sentry events — framework-agnostic.
//
// CONSUMER: shared (web @sentry/react init + worker @sentry/cloudflare
// init both call this in beforeSend).
//
// This system holds ~19k people's SSNs + financial data, so error
// reports must never carry PII to a third party. Defense-in-depth on
// top of sendDefaultPii:false:
//   * Drop request body / cookies / headers / query string entirely.
//   * Keep only an opaque user id (never email / username / IP).
//   * Drop free-form `extra` (can hold record data).
//   * Redact SSN- and email-looking substrings from error TEXT.
//
// Typed against a minimal structural shape so it depends on no specific
// @sentry/* package. Mutates in place AND returns the same ref so a
// beforeSend can do `(event) => scrubSentryEvent(event)`.

const PII_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\b\d{3}-\d{2}-\d{4}\b/g, label: "[ssn]" }, // 123-45-6789
  { re: /\b\d{9}\b/g, label: "[redacted-9]" }, // bare 9-digit run (SSN/acct)
  {
    re: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
    label: "[email]",
  },
];

export function redactPii(s: string): string {
  let out = s;
  for (const p of PII_PATTERNS) out = out.replace(p.re, p.label);
  return out;
}

export interface MinimalSentryEvent {
  message?: unknown;
  request?: {
    data?: unknown;
    cookies?: unknown;
    headers?: unknown;
    query_string?: unknown;
    url?: unknown;
  } | null;
  user?: { id?: unknown } | Record<string, unknown> | null;
  exception?: { values?: { value?: unknown }[] } | null;
  extra?: Record<string, unknown> | null;
}

export function scrubSentryEvent<T extends MinimalSentryEvent>(event: T): T {
  // Request body / cookies / headers / query string are the highest
  // PII risk — drop them outright (keep the path-only url).
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
  }

  // Keep only an opaque user id.
  if (event.user && typeof event.user === "object") {
    const id = (event.user as { id?: unknown }).id;
    (event as MinimalSentryEvent).user = id !== undefined ? { id } : null;
  }

  // Free-form extras can carry record data.
  if (event.extra) (event as MinimalSentryEvent).extra = null;

  // Redact PII-looking substrings from human-readable error text.
  if (typeof event.message === "string") {
    (event as MinimalSentryEvent).message = redactPii(event.message);
  }
  const vals = event.exception?.values;
  if (Array.isArray(vals)) {
    for (const v of vals) {
      if (v && typeof v.value === "string") v.value = redactPii(v.value);
    }
  }

  return event;
}
